// src/components/Services.jsx (Updated)

import React, { useState, useEffect } from "react";
import {
  FaSearch,
  FaSignOutAlt,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import CompanyLogo from "../../assets/images/Futuresoft_logo.jpg";
import { resetData } from "../../features/login/LoginSlice";
import usePermission from "../../utils/usePermission";
import { useServices } from "../../customHooks/Services/useServices";
import "bootstrap/dist/css/bootstrap.min.css";

// Utility functions for store grouping display logic
const getTypeColor = (type) => {
  // Logic to determine color based on type string (e.g., 'Retail - MSN')
  const shortType = type.toUpperCase().includes("MSN") ? "MSN" : type.toUpperCase().includes("ISN") ? "ISN" : "OTHER";
  
  switch (shortType) {
    case "MSN":
      return "primary";
    case "ISN":
      return "success";
    default:
      return "secondary";
  }
};

const getTypeDisplayName = (fullType) => {
  if (fullType.includes("MSN")) return "MSN";
  if (fullType.includes("ISN")) return "ISN";
  return fullType;
};

const Services = () => {
  const {
    stores, // This is now the grouped object (e.g., { 'Retail - MSN': [...], 'Retail - ISN': [...] })
    loading,
    error,
    search,
    totalCount,
    handleStoreSearch,
    initiallyHadData,
    authData,
    handleChangeStore,
  } = useServices(); // This uses the RSF service by default now

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authData) {
      navigate("/login", { replace: true });
    }
  }, [authData, navigate]);

  const canRead = usePermission("MULTI_STORES");

  useEffect(() => {
    if (!canRead) {
      navigate("/orders", { replace: true });
    }
  }, [canRead, navigate]);

  const handleLogout = () => {
    dispatch(resetData());
  };

  const handleStoreSelect = (store) => {
    handleChangeStore(store);
  };

  // CORRECTED: Use the stores object directly for iteration
  const groupedStores = stores;

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
        position: "relative",
      }}
    >
      {/* Animated background pattern */}
      <div
        className="position-absolute w-100 h-100"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(0,123,255,0.1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
          opacity: 0.5,
          zIndex: 1,
        }}
      ></div>

      {/* Header */}
      <div className="position-relative" style={{ zIndex: 2 }}>
        <div className="container-fluid px-4 py-3">
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className="d-flex align-items-center">
                <img
                  src={CompanyLogo}
                  alt="Futuresoft Logo"
                  className="company-logo"
                />
              </div>
            </div>
            <div className="col-md-6 text-md-end mt-3 mt-md-0">
              <button
                className="btn btn-outline-secondary btn-lg rounded-pill px-4"
                onClick={handleLogout}
              >
                <FaSignOutAlt className="me-2" />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container">
          <div className="row mt-4">
            {/* Left: Sidebar with scrollable tenant cards */}

            <div
              className="bg-white p-3 rounded shadow-sm"
              style={{ height: "80vh", overflowY: "auto" }}
            >
              <h5 className="fw-bold mb-3 text-primary">Available Tenants</h5>

              {/* Search Input */}
              <div className="mb-3 position-relative">
                <FaSearch
                  className="position-absolute text-muted"
                  style={{
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
                <input
                  type="text"
                  className="form-control ps-4"
                  placeholder="&nbsp;Search tenants..."
                  value={search}
                  onChange={handleStoreSearch}
                />
              </div>
                
              {/* Loading/Error state */}
              {loading && <div className="text-center py-3 text-info">Loading tenants...</div>}
              {error && <div className="text-center py-3 text-danger">Error: {error}</div>}

              {/* Tenant Cards Grouped Accordion Style */}
              <div className="accordion" id="tenantAccordion">
                {Object.entries(groupedStores).map(([type, storeList], idx) => {
                  // Ensure storeList is an array before processing
                  if (!Array.isArray(storeList) || storeList.length === 0) return null;

                  const typeKey = getTypeDisplayName(type);
                  const collapseId = `collapse-${idx}`;
                  const headingId = `heading-${idx}`;
                  return (
                    <div className="accordion-item mb-2" key={type}>
                      <h2 className="accordion-header" id={headingId}>
                        <button
                          className={`accordion-button ${
                            idx !== 0 ? "collapsed" : ""
                          } fw-bold text-${getTypeColor(typeKey)}`}
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#${collapseId}`}
                          aria-expanded={idx === 0}
                          aria-controls={collapseId}
                        >
                          {type} ({storeList.length})
                        </button>
                      </h2>
                      <div
                        id={collapseId}
                        className={`accordion-collapse collapse ${
                          idx === 0 ? "show" : ""
                        }`}
                        aria-labelledby={headingId}
                        data-bs-parent="#tenantAccordion"
                      >
                        <div className="accordion-body p-2">
                          {storeList.map((store) => (
                            <div
                              key={store.id}
                              className="card mb-2 border-0 shadow-sm"
                              onClick={() => handleStoreSelect(store)}
                              style={{ cursor: "pointer" }}
                            >
                              <div className="card-body d-flex align-items-center p-2">
                                <img
                                  src={store.brand_logo}
                                  alt={store.brand_name}
                                  className="me-3 rounded border"
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    objectFit: "cover",
                                  }}
                                  onError={(e) => {
                                    e.target.src =
                                      "https://via.placeholder.com/60";
                                  }}
                                />
                                <div className="flex-grow-1">
                                  <h6 className="mb-0 text-truncate">
                                    {store.brand_name}
                                  </h6>
                                  <small className="text-muted">
                                    ID: {store.id}
                                  </small>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Fallback for no stores */}
              {!loading && Object.keys(groupedStores).length === 0 && (
                  <div className="text-muted text-center py-3">
                      No stores available.
                  </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;